import { useMemo, useState } from 'react';
import { SortOrder, TableColumn } from 'react-data-table-component';
import { useAppSelector } from 'redux/merchant/hooks';
import getErrorMessage from 'utils/getErrorMessage';

import { Button, FlexContainer, Heading, Icon } from '@limepayments/cosmic';

import { PaymentExportColumnsObj } from '../../constants';
import { IExportFilterObj, TransactionsQueryEdges, getFilterCount } from '../types';
import ExportComponent from './ExportComponent';
import FilterComponent from './FilterComponent';
import PaymentsDataTable from './PaymentsDataTable';

const getInitialExportState = (): IExportFilterObj => {
  return {
    exportColumnsSelectedList: [],
    exportDataLoader: false,
    exportDataCSV: null,
    exportSuccessfully: false,
    errorMessage: '',
  };
};

export interface Props {
  paymentList: TransactionsQueryEdges;
  fetchListLoader: boolean;
  totalRows: number;
  handlePerRowsChange: (newPerPage: number, page: number) => void;
  handlePageChange: (page: number) => void;
  limit: number;
  toggleFilter: () => void;
  filterPopupOpen: boolean;
  activePage: number;
  handleSort: (selectedColumn: TableColumn<TransactionsQueryEdges[number]>, sortDirection: SortOrder) => void;
  handleExport: (columns: string[]) => Promise<string>;
  defaultExportOpened?: boolean;
}

function PaymentsList({
  paymentList,
  totalRows,
  limit,
  fetchListLoader,
  handlePageChange,
  handlePerRowsChange,
  toggleFilter,
  activePage,
  handleSort,
  handleExport,
  defaultExportOpened,
  filterPopupOpen,
}: Props) {
  const [isExportOpened, setIsExportOpened] = useState(defaultExportOpened ?? false);

  // Export variables
  const [exportFilterObj, setExportFilterObj] = useState<IExportFilterObj>(getInitialExportState());

  const { paymentFilterObj } = useAppSelector((state) => ({
    paymentFilterObj: state.payment.paymentFilterObj,
  }));

  const filterCount = useMemo(() => getFilterCount(paymentFilterObj), [paymentFilterObj]);

  const toggleExportHandler = (val: boolean, closeSection?: boolean) => {
    if (val) {
      resetExportValuesHandler();
    }

    if (closeSection) {
      toggleExport();
    }
  };

  const resetExportValuesHandler = () => {
    setExportFilterObj(getInitialExportState());
  };

  const exportSaveClickHandler = async () => {
    try {
      if (exportFilterObj.exportColumnsSelectedList.length < 1) {
        setExportFilterObj((prevState) => ({
          ...prevState,
          errorMessage: getErrorMessage('Please select at least one column to export data'),
        }));
        return;
      }

      setExportFilterObj((prevState) => ({
        ...prevState,
        exportDataLoader: true,
        errorMessage: '',
      }));

      const response = await handleExport(exportFilterObj.exportColumnsSelectedList);

      setExportFilterObj((prevState) => ({
        ...prevState,
        exportDataCSV: response,
        exportSuccessfully: true,
      }));
    } catch (error) {
      setExportFilterObj((prevState) => ({
        ...prevState,
        errorMessage: getErrorMessage(error),
      }));
    } finally {
      setExportFilterObj((prevState) => ({
        ...prevState,
        exportDataLoader: false,
      }));
    }
  };

  const exportColumnsSelectedListHandler = (val: string, isSelectAll?: boolean) => {
    let updatedList = [...exportFilterObj.exportColumnsSelectedList];

    if (!val && isSelectAll) {
      updatedList =
        exportFilterObj.exportColumnsSelectedList.length === Object.keys(PaymentExportColumnsObj).length
          ? []
          : Object.keys(PaymentExportColumnsObj).map((obj) => obj);
    }

    if (val && exportFilterObj.exportColumnsSelectedList.includes(val)) {
      updatedList = exportFilterObj.exportColumnsSelectedList.filter((obj) => {
        return obj !== val;
      });
    }

    if (val && !exportFilterObj.exportColumnsSelectedList.includes(val)) {
      updatedList.push(val);
    }

    setExportFilterObj((prevState) => ({
      ...prevState,
      exportColumnsSelectedList: updatedList,
    }));
  };

  const toggleExport = () => {
    setIsExportOpened((wasExportOpened) => !wasExportOpened);
  };

  return (
    <div className="content-wrap-inner">
      <div className="lp-w-full lp-flex">
        <div className="payment-content-area no-sidebar">
          <div className="lp-w-full lp-flex lp-justify-between sm-lp-flex-col">
            <Heading alignment="left" tagName="h2" variant="sm">
              Payments
            </Heading>
            <div className="merchant-controls sm-mt-16">
              <FlexContainer classNames="lp-flex">
                <div className="lp-flex order-2 position-relative">
                  <Button
                    className="lp-w-full"
                    onClick={() => toggleFilter()}
                    size="small"
                    variant="reversed"
                    disabled={paymentList.length < 1 && filterCount === 0}
                    testId="payment-filter-button"
                  >
                    <Icon name="Filter" className="ml-0" />
                    Filter ({filterCount})
                  </Button>
                  <Button
                    className="lp-w-full"
                    onClick={toggleExport}
                    size="small"
                    variant="reversed"
                    disabled={paymentList.length < 1}
                    testId="payment-export-button"
                  >
                    <Icon name="ExternalLink" className="ml-0" />
                    Export
                  </Button>

                  {/* Filter Dropdown */}
                  {filterPopupOpen && (
                    <FilterComponent modalToggler={toggleFilter} handlePageChange={handlePageChange} />
                  )}

                  {/* Export Dropdown */}
                  {isExportOpened && (
                    <div className="filter-dropdown w-656">
                      <ExportComponent
                        onCloseExportClick={toggleExportHandler}
                        exportSaveClickHandler={exportSaveClickHandler}
                        exportFilterObj={exportFilterObj}
                        setExportColumnsSelectedList={exportColumnsSelectedListHandler}
                        filterCount={filterCount}
                        totalRows={totalRows}
                      />
                    </div>
                  )}
                </div>
              </FlexContainer>
            </div>
          </div>

          <PaymentsDataTable
            paymentList={paymentList}
            fetchListLoader={fetchListLoader}
            totalRows={totalRows}
            handlePerRowsChange={handlePerRowsChange}
            handlePageChange={handlePageChange}
            limit={limit}
            activePage={activePage}
            handleSort={handleSort}
            isFilterRequest={filterCount > 0}
            toggleFilter={toggleFilter}
          />
        </div>
      </div>
    </div>
  );
}

export default PaymentsList;
